/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  makeStyles,
  withStyles,
  useMediaQuery,
  Box,
  Divider,
  Typography,
  Paper,
  InputBase,
  IconButton,
} from "@material-ui/core";

import SelectorComponent from "app/common/SelectorComponent";
import { useSelector } from "react-redux";
import { AutoSizer, List } from "react-virtualized";
import ScenarioOptionComponent from "app/scenarios/ScenarioOptionComponent";
import _ from "lodash";
import { useDebounce } from "lib/react/customHooks";
import getDefaultCycle from "lib/utils/cycles";
import { GoSearch } from "react-icons/go";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
  dialogContent: {
    height: "750px",
    flexDirection: "column",
    display: "flex",
    flexGrow: "1",
    padding: "0px",
    width: "100%",
  },
  dialogContentSmall: {
    height: "650px",
    flexDirection: "column",
    display: "flex",
    flexGrow: "1",
    padding: "0px",
    width: "100%",
  },
  dialogRoot: {
    width: "750px",
  },
  dialogText: {
    marginTop: "15px",
    marginBottom: "15px",
    paddingLeft: "25px",
  },
  selectDivider: {
    backgroundColor: "#e2e2e2",
  },
  starredScenario: {
    borderLeft: "5px solid #e86861",
  },
  scenarioSelectorList: {
    "&:focus": {
      outline: "none",
    },
    "&::-webkit-scrollbar": {
      width: "5px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },

    /* Handle */
    "&::-webkit-scrollbar-thumb": {
      background: "#bbb",
      borderRadius: "5px",
      padding: "1px",
    },

    /* Handle on hover */
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#999",
    },
  },
  formContainer: {
    boxShadow: "none",
    alignItems: "center",
    width: "100%",
    height: "150px",
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
    // color: "#fff",
    fontSize: "0.875rem",
  },
  iconButton: {
    padding: 10,
  },
  divider: {
    height: 28,
    margin: 4,
    backgroundColor: "white",
  },
  searchButton: {
    "&:hover": {
      cursor: "auto",
    },
  },
  noScenariosFoundText: {
    padding: "10px 10px 10px 10px",
  },
  formContent: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridTemplateRows: "2fr 2fr 3fr",
    width: "100%",
    alignItems: "end",
    gap: "10px",
    boxShadow: "none",
  },
  formTitle: {
    gridColumn: "1/3",
    marginLeft: "15px",
  },
  selectorsContainer: {
    display: "flex",
    margin: "0 15px",
    "&>*": {
      width: "50%",
    },
  },
  searchContainer: {
    gridColumn: "1/3",
    borderTop: "1px solid #e0e0e0",
    borderBottom: "1px solid #e0e0e0",
    height: "100%",
    display: "flex",
  },
  customSearch: {
    margin: "auto",
    width: "95%",
    height: "100%",
    overflow: "hidden",
  },
}));

const StyledDialogTitle = withStyles(() => ({
  root: {
    padding: "0px",
  },
}))(DialogTitle);

const MainConfigurationDialogComponent = ({
  open = false,
  handleClose,
  setMainScenarioData,
  setBaseCompetitorScenarioData,
  setBaseCompetitorKpis,
  cyclesWithCompetitorScenarios,
}) => {
  const classes = useStyles();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const scenariosSlice = useSelector((state) => state.scenarios);
  const metadataSlice = useSelector((state) => state.metadata);
  const cycleSlice = useSelector((state) => state.cycle);
  const [searchTerm, setSearchTerm] = React.useState("");

  const typeCicleOptions = [
    { label: "Trade Promotions", cycleTypes: [2, 4], key: 0 },
    { label: "Pricing", cycleTypes: [1, 3], key: 1 },
  ];
  const getCycleTypeOption = (cycleTypeId) => {
    if (_.includes(typeCicleOptions[0].cycleTypes, cycleTypeId))
      return typeCicleOptions[0];
    return {};
  };

  const getDefaultCycleOption = (cycleTypeOption) => {
    const defaultCycleId = getDefaultCycle(
      _.filter(cycleSlice.list, (cycle) =>
        cycleTypeOption.cycleTypes.includes(cycle.cycle_type_id)
      )
    );
    return {
      key: defaultCycleId,
      label: _.find(cycleSlice.list, (cycle) => defaultCycleId === cycle.id)
        .label,
    };
  };
  const defaultSelectedCycle = getDefaultCycleOption(typeCicleOptions[0]);

  const [selectedTypeCycle, setSelectedTypeCycle] = useState(
    typeCicleOptions[0]
  );
  const [selectedCycle, setSelectedCycle] = useState(defaultSelectedCycle);

  const [visibleScenarios, setVisibleScenarios] = React.useState(
    scenariosSlice.totalsByScenarioId
  );

  const handleInputChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleTypeCycleChange = (option) => {
    if (option.key !== selectedTypeCycle.key) setSelectedTypeCycle(option);
  };

  const handleCycleChange = (option) => {
    if (option.key !== selectedCycle.key) setSelectedCycle(option);
  };

  const debouncedSearchTerm = useDebounce(searchTerm, 200);

  useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const filteredScenarios = _.filter(
      scenariosSlice.totalsByScenarioId,
      (scenarioTotal) => {
        const checkIsCycleTypeOfSelectedCycle =
          getCycleTypeOption(scenarioTotal.scenario.cycle.cycle_type_id)
            ?.key === selectedTypeCycle.key;
        const checkIsScenarioInSelectedCycle =
          scenarioTotal.scenario.cycle.id === selectedCycle.key ||
          _.isEmpty(selectedCycle);
        const checkIsSearchBoxMatch = _.some(
          scenarioTotal.scenario,
          (value) => {
            return (
              typeof value === "string" &&
              value.toLowerCase().includes(searchTerm.toLowerCase())
            );
          }
        );
        return (
          checkIsCycleTypeOfSelectedCycle &&
          checkIsScenarioInSelectedCycle &&
          checkIsSearchBoxMatch
        );
      }
    );

    setVisibleScenarios(filteredScenarios);
  }, [
    searchTerm,
    selectedCycle,
    selectedTypeCycle,
    scenariosSlice.totalsByScenarioId,
  ]);

  useEffect(() => {
    setSelectedCycle(
      getDefaultCycleOption(
        _.find(
          typeCicleOptions,
          (option) => option.key === selectedTypeCycle.key
        )
      )
    );
  }, [selectedTypeCycle]);

  const handleCloseButton = () => {
    setSearchTerm("");
    setSelectedTypeCycle(typeCicleOptions[0]);
    setSelectedCycle(defaultSelectedCycle);
    handleClose();
  };

  const cycleTypes = _.reduce(
    cycleSlice.list,
    (prev, currCycle) => {
      const currentCycleTypeOption = getCycleTypeOption(
        currCycle.cycle_type_id
      );
      if (
        !_.isEmpty(currentCycleTypeOption) &&
        !_.find(prev, currentCycleTypeOption)
      ) {
        prev.push(currentCycleTypeOption);
      }
      return prev;
    },
    []
  );
  const avabileCyclesOptions = _.reduce(
    cycleSlice.list,
    (prev, cycle) => {
      const checkIsCycleTypeInSelectedType =
        getCycleTypeOption(cycle.cycle_type_id)?.key === selectedTypeCycle.key;
      const checkIsCycleInCyclesWithEstimations = _.find(
        cyclesWithCompetitorScenarios,
        (c) => c.cycle_id === cycle.id
      );
      if (
        checkIsCycleTypeInSelectedType &&
        checkIsCycleInCyclesWithEstimations
      ) {
        prev.push({ key: cycle.id, label: cycle.label });
      }
      return prev;
    },
    []
  );
  const rowRender = ({ index, key, style }) => (
    <div
      key={key}
      style={style}
      className={
        visibleScenarios[index].scenario.is_starred && classes.starredScenario
      }
    >
      <ScenarioOptionComponent
        scenarioTotal={visibleScenarios[index]}
        visibleScenarios={visibleScenarios}
        referenceScenarioTotal={_.find(
          scenariosSlice.totalsByScenarioId,
          (scenarioTotal) =>
            scenarioTotal.scenario.scenario_type_id === 1 &&
            scenarioTotal.scenario.cycle_id ===
              visibleScenarios[0].scenario.cycle_id
        )}
        selectedScenario={scenariosSlice.selectedScenario}
        metadata={metadataSlice}
        closeParentPopover={handleCloseButton}
        haveShortcutsBar={false}
        showPercentages={false}
        setMainScenarioData={setMainScenarioData}
        setBaseCompetitorScenarioData={setBaseCompetitorScenarioData}
        setBaseCompetitorKpis={setBaseCompetitorKpis}
        isCompetitors
      />
      <Divider className={classes.selectDivider} />
    </div>
  );
  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      disableRestoreFocus
      classes={{
        paperWidthMd: classes.dialogRoot,
      }}
      onClose={handleCloseButton}
    >
      <StyledDialogTitle className={classes.dialogTitle}>
        <Box className={classes.dialogText}>Base scenario</Box>
      </StyledDialogTitle>
      <DialogContent
        className={
          isSmallScreen ? classes.dialogContentSmall : classes.dialogContent
        }
      >
        <form
          className={classes.formContainer}
          onSubmit={(event) => event.preventDefault()}
          noValidate
          autoComplete="off"
        >
          <Paper className={classes.formContent}>
            <Typography variant="body2" className={classes.formTitle}>
              Add base scenario
            </Typography>

            <Box className={classes.selectorsContainer}>
              {/* Cycle Type Selector */}
              <SelectorComponent
                defaultOptionKey={selectedTypeCycle.key}
                options={cycleTypes}
                title="Cycle Type"
                onChange={handleTypeCycleChange}
              />

              {/* Cycle Selector */}
              <SelectorComponent
                defaultOptionKey={selectedCycle.key}
                options={avabileCyclesOptions}
                title="Cycle"
                onChange={handleCycleChange}
              />
            </Box>

            {/* Search */}
            <Box className={classes.searchContainer}>
              <IconButton
                type="button"
                disableRipple
                disableFocusRipple
                className={`${classes.iconButton} ${classes.searchButton}`}
                aria-label="search"
              >
                <GoSearch size={18} />
              </IconButton>
              <InputBase
                className={classes.customSearch}
                placeholder="Search scenarios"
                inputProps={{ "aria-label": "search scenarios" }}
                value={searchTerm}
                onChange={handleInputChange}
              />
            </Box>
          </Paper>
        </form>
        {visibleScenarios.length > 0 ? (
          <AutoSizer>
            {({ width, height }) => (
              <List
                className={classes.scenarioSelectorList}
                width={width}
                height={height - 155} // We have to substract the height of the list header
                rowHeight={isSmallScreen ? 130 : 125}
                rowCount={visibleScenarios.length}
                rowRenderer={rowRender}
              />
            )}
          </AutoSizer>
        ) : (
          <Typography variant="body2" className={classes.noScenariosFoundText}>
            No custom scenarios where found with the specified criteria.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MainConfigurationDialogComponent;
